import { ContactNumberType } from '@/enums';

/**
 * Checks whether the given contact number string is valid for the given contact number type.
 *
 * @param {string|undefined|null} number - The contact number string to check.
 * @param {object} type - The type of contact number, e.g. 'Mobile', 'Work' or 'Home'.
 * @return {boolean} - True if the contact number string is valid for the given contact number type, or false otherwise.
 */
export const validContactNumber = (number, type) => {
  const LANDLINE_AREA_CODES = ['02', '03', '07', '08'];
  const LANDLINE_REGEX = /^\d{2}(\s\d{4}){2}$/;
  const MOBILE_NUMBER_PREFIX = '04';
  const MOBILE_REGEX = /^\d{4}(\s\d{3}){2}$/;

  if (number == null) {
    return false;
  }

  const isValidLandlineNumber = (contactNumber) => {
    return (
      LANDLINE_REGEX.test(contactNumber) &&
      LANDLINE_AREA_CODES.some((element) => {
        return contactNumber.startsWith(element);
      })
    );
  };

  const isValidMobileNumber = (contactNumber) => {
    return MOBILE_REGEX.test(contactNumber) && contactNumber.startsWith(MOBILE_NUMBER_PREFIX);
  };

  // To be a valid contact number, it must be 12 characters in length and include only numbers and spaces.
  // Refer to regex above for more details.
  switch (type.toLowerCase()) {
    case ContactNumberType.WORK:
    case ContactNumberType.HOME:
      return isValidLandlineNumber(number) || isValidMobileNumber(number);
    case ContactNumberType.MOBILE:
      return isValidMobileNumber(number);
    default:
    // Do nothing
  }

  return false;
};

export const getNumberPlaceholder = (type) => {
  switch (type?.toLowerCase()) {
    case ContactNumberType.MOBILE:
      return 'e.g. 0400 ### ###';
    case ContactNumberType.HOME:
      return 'e.g. 03 9400 ####';
    default:
      // Work
      return 'e.g. 07 3800 ####';
  }
};

export const getNumberMask = (number, type) => {
  // Number which begins with 04 is Australian wide mobile number, the rest is local phone lines which have a different format
  const MOBILE_NUMBER_PREFIX = '04';
  const MOBILE_REGEX = /^\d{4}(\s\d{3}){2}$/;
  const LANDLINE_REGEX = /^\d{2}(\s\d{4}){2}$/;
  const MOBILE_NUMBER_FORMAT = '#### ### ###';
  const LANDLINE_NUMBER_FORMAT = '## #### ####';

  switch (type?.toLowerCase()) {
    case ContactNumberType.MOBILE:
      return MOBILE_NUMBER_FORMAT;
    case ContactNumberType.WORK:
    case ContactNumberType.HOME:
      // If user had pasted a mobile number OR began to type the mobile number prefix, then switch the masking format to mobile.
      if ((MOBILE_REGEX.test(number) && number?.startsWith(MOBILE_NUMBER_PREFIX)) || (LANDLINE_REGEX.test(number) && number?.startsWith(MOBILE_NUMBER_PREFIX))) {
        return MOBILE_NUMBER_FORMAT;
      }
      return LANDLINE_NUMBER_FORMAT;
    default:
      // If no contact number type selected, then display mobile format if the mobile prefix was entered.
      if (number?.startsWith(MOBILE_NUMBER_PREFIX)) {
        return MOBILE_NUMBER_FORMAT;
      }
      return LANDLINE_NUMBER_FORMAT;
  }
};
